/**
 * Spotlight scrim (PEL-516; modal/blocking update PEL-516, 2026-06-23).
 *
 * Dims the whole viewport except a transparent "hole" over the anchor, using the
 * big-box-shadow technique for the visual dim. The dim layer keeps
 * `pointer-events: none` (the box-shadow region is not hit-testable anyway).
 *
 * INTERACTION behaviour: informational steps place a full-screen interaction
 * blocker behind the coachmark, including over the transparent visual cutout.
 * Action steps tile four blockers around the anchor's exact bounds, leaving only
 * that target reachable. Visual spotlight padding never expands the clickable
 * aperture.
 *
 * CLIP: when `clipToScrollParent`, the cutout is intersected with the anchor's
 * nearest scrollable ancestor's rect, so a highlight taller than its scroll
 * viewport (the job-detail overlay's Match Analysis section) can't bleed past the
 * scroll boundary. Its bottom is further clipped to the top of any bottom-pinned
 * `sticky` layer inside that scroll area (the overlay's Generate / Learn action
 * band, which floats over the content) so the highlight stops at that fixed
 * layer's boundary instead of running underneath it. The frame is rebuilt from the
 * clamped hole, so blocking matches the clipped highlight.
 *
 * The Spotlight is only mounted when a coachmark is actually rendered on a present
 * anchor — so a waiting/event-gated step shows no scrim and never freezes the app.
 *
 * The cutout (and the frame) follow the anchor every animation frame, including
 * when the step re-anchors from the list card to the job-detail overlay (the host
 * remounts on `${stepId}:${layer}`, handing us the now-active element).
 */

import { useEffect, useRef } from 'react'
import { findBottomStickyDescendant, findScrollableAncestor, useAnchorRect } from './hooks'

const PADDING = 6
const RADIUS = 12
const SCRIM = 'rgba(16, 42, 86, 0.55)' // brand navy (#102A56)

export function Spotlight({
  element,
  allowAnchorInteraction = false,
  clipToScrollParent = false,
  scrollPassthrough = true,
  padding = PADDING,
}: {
  element: HTMLElement
  /** Leave the anchor's exact visible bounds open for its intended action. */
  allowAnchorInteraction?: boolean
  clipToScrollParent?: boolean
  /**
   * Still let wheel/touch scroll pass through the dim area to the
   * anchor's scroll container, so the user can scroll the whole page while only
   * clicks are trapped (spec §2.2 keeps the coachmark up on outside interaction,
   * but scrolling should not get stuck to the highlighted section). Disabled for
   * scroll-locked steps, which intentionally freeze the surface. Defaults on.
   */
  scrollPassthrough?: boolean
  /** Cutout padding (px) around the anchor. Defaults to the base PADDING. */
  padding?: number
}) {
  const rect = useAnchorRect(element)
  // Forward scroll from the click-trapping frame to the anchor's scroll container.
  // The four blocking rects have `pointer-events: auto` (to swallow clicks), which
  // also swallows wheel/touch — and being in a body portal, the browser routes
  // their scroll to the (non-scrollable) body instead of the page's real scroller.
  // Without this, only the open hole scrolls and the user gets stuck. Native
  // non-passive listeners (React's onWheel/onTouchMove are passive, so they can't
  // preventDefault the no-op body scroll) forward the delta and swallow the default.
  const blockerRef = useRef<HTMLDivElement>(null)
  // The blocker only exists once there's a rect to render (see the `!rect` guard
  // below), so re-run when that flips true — without this the effect fires once
  // while `blockerRef` is still null and never re-attaches. A boolean keeps it
  // from thrashing on every per-frame rect identity change.
  const hasRect = rect != null
  useEffect(() => {
    const node = blockerRef.current
    if (!node || !hasRect || !scrollPassthrough) return
    const container = findScrollableAncestor(element)
    if (!container) return

    const onWheel = (e: WheelEvent) => {
      // deltaMode: 0=pixel, 1=line, 2=page — normalise line/page to pixels.
      const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? container.clientHeight : 1
      container.scrollTop += e.deltaY * factor
      container.scrollLeft += e.deltaX * factor
      e.preventDefault()
    }

    let lastX = 0
    let lastY = 0
    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0]
      if (t) {
        lastX = t.clientX
        lastY = t.clientY
      }
    }
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0]
      if (!t) return
      container.scrollTop += lastY - t.clientY
      container.scrollLeft += lastX - t.clientX
      lastX = t.clientX
      lastY = t.clientY
      e.preventDefault()
    }

    node.addEventListener('wheel', onWheel, { passive: false })
    node.addEventListener('touchstart', onTouchStart, { passive: false })
    node.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => {
      node.removeEventListener('wheel', onWheel)
      node.removeEventListener('touchstart', onTouchStart)
      node.removeEventListener('touchmove', onTouchMove)
    }
  }, [element, scrollPassthrough, hasRect])
  // Clip boundary: the anchor's scroll viewport, and the bottom-pinned sticky layer
  // within it (the overlay's Generate/Learn action band) that the highlight must
  // stop above. Both resolved at render (stable DOM node references) and tracked
  // per-frame like the anchor so they stay correct through the entrance animation.
  // Null when not requested / no scroller / no sticky layer.
  const clipElement = clipToScrollParent ? findScrollableAncestor(element) : null
  const clipRect = useAnchorRect(clipElement)
  const stickyFooter = clipToScrollParent ? findBottomStickyDescendant(clipElement) : null
  const stickyRect = useAnchorRect(stickyFooter)

  if (!rect) return null

  // Match the cutout's corner radius to the anchored element so the highlight
  // hugs its real shape (a rounded-3xl card vs a small button), kept concentric
  // with the padding. Falls back when the radius is unreadable (e.g. jsdom).
  const elementRadius = Number.parseFloat(getComputedStyle(element).borderRadius)
  const radius = (Number.isNaN(elementRadius) ? RADIUS : elementRadius) + padding

  // Cutout edges in viewport coordinates (rect is from getBoundingClientRect, and
  // this layer is position:fixed, so the two share a coordinate space).
  let top = rect.top - padding
  let left = rect.left - padding
  let right = rect.left + rect.width + padding
  let bottom = rect.top + rect.height + padding

  // Intersect with the scroll viewport so the highlight can't extend past the
  // scroll boundary, and clip the bottom to the top of a bottom-pinned sticky
  // layer (the overlay action band) so it stops at that fixed layer rather than
  // running underneath it.
  if (clipRect) {
    top = Math.max(top, clipRect.top)
    left = Math.max(left, clipRect.left)
    right = Math.min(right, clipRect.left + clipRect.width)
    const viewportBottom = clipRect.top + clipRect.height
    const clipBottom = stickyRect ? Math.min(viewportBottom, stickyRect.top) : viewportBottom
    bottom = Math.min(bottom, clipBottom)
  }

  const holeTop = top
  const holeLeft = left
  const holeWidth = Math.max(0, right - left)
  const holeHeight = Math.max(0, bottom - top)
  // Unlike the visual hole, the interactive opening has no decorative padding.
  // Clip it to the same scroll viewport/sticky boundary and to the viewport so a
  // partially visible target cannot expose an unrelated neighboring control.
  const interactionTop = Math.max(0, rect.top, clipRect?.top ?? 0)
  const interactionLeft = Math.max(0, rect.left, clipRect?.left ?? 0)
  const interactionRight = Math.min(
    window.innerWidth,
    rect.left + rect.width,
    clipRect ? clipRect.left + clipRect.width : window.innerWidth,
  )
  const interactionBottom = Math.min(
    window.innerHeight,
    rect.top + rect.height,
    clipRect ? clipRect.top + clipRect.height : window.innerHeight,
    stickyRect ? stickyRect.top : window.innerHeight,
  )
  const interactionHeight = Math.max(0, interactionBottom - interactionTop)

  return (
    <div className="pointer-events-none fixed inset-0 z-[60]" aria-hidden="true">
      {/* Visual dim — pointer-events:none. No geometry transition: the cutout is
          re-measured every animation frame (useAnchorRect), so a CSS transition on
          top would lag a frame behind the anchor on scroll (chasing it) instead of
          tracking it. Position/size track instantly; only the step-to-step swap
          looks like a jump, which matches the coachmark (floating-ui repositions
          instantly too). */}
      <div
        className="absolute"
        style={{
          top: holeTop,
          left: holeLeft,
          width: holeWidth,
          height: holeHeight,
          borderRadius: radius,
          boxShadow: `0 0 0 9999px ${SCRIM}`,
        }}
      />

      <div ref={blockerRef} data-testid="ftue-scrim-blocker" className="pointer-events-none">
        {allowAnchorInteraction ? (
          <>
            <div
              className="pointer-events-auto fixed left-0 right-0"
              style={{ top: 0, height: interactionTop }}
            />
            <div
              className="pointer-events-auto fixed bottom-0 left-0 right-0"
              style={{ top: interactionBottom }}
            />
            <div
              className="pointer-events-auto fixed left-0"
              style={{
                top: interactionTop,
                width: interactionLeft,
                height: interactionHeight,
              }}
            />
            <div
              className="pointer-events-auto fixed right-0"
              style={{
                top: interactionTop,
                left: interactionRight,
                height: interactionHeight,
              }}
            />
          </>
        ) : (
          <div className="pointer-events-auto fixed inset-0" />
        )}
      </div>
    </div>
  )
}
